import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const BookCarousel = ({ addToRefs }) => {
  const { t } = useTranslation();

  useEffect(() => {
    // Initialize Bootstrap carousel
    const carouselElement = document.querySelector('#cbBookCarousel');
    if (carouselElement && window.bootstrap?.Carousel) {
      new window.bootstrap.Carousel(carouselElement, {
        interval: 5000,
        ride: 'carousel',
      });
    } else {
      console.error('Bootstrap Carousel is not available. Ensure Bootstrap JS is loaded.');
    }

    // Custom 3D tilt effect (disabled on mobile)
    const tiltElements = document.querySelectorAll('.cb-book-tilt');
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    if (!isMobile) {
      tiltElements.forEach((element) => {
        const handleMouseMove = (e) => {
          const rect = element.getBoundingClientRect();
          const centerX = rect.left + rect.width / 2;
          const centerY = rect.top + rect.height / 2;
          const mouseX = e.clientX - centerX;
          const mouseY = e.clientY - centerY;

          const maxTilt = 10;
          const rotateY = (mouseX / rect.width) * maxTilt;
          const rotateX = -(mouseY / rect.height) * maxTilt;

          element.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.05)`;
        };

        const handleMouseEnter = () => {
          element.style.transition = 'transform 0.1s ease';
        };

        const handleMouseLeave = () => {
          element.style.transition = 'transform 0.3s ease';
          element.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)';
        };

        element.addEventListener('mousemove', handleMouseMove);
        element.addEventListener('mouseenter', handleMouseEnter);
        element.addEventListener('mouseleave', handleMouseLeave);

        return () => {
          element.removeEventListener('mousemove', handleMouseMove);
          element.removeEventListener('mouseenter', handleMouseEnter);
          element.removeEventListener('mouseleave', handleMouseLeave);
        };
      });
    }
  }, []);

  const bookSlides = [
    [
      { src: '/images/titles-alternate/001.jpg', alt: t('bookCarousel.bookAlts.book1') },
      { src: '/images/titles-alternate/002.jpg', alt: t('bookCarousel.bookAlts.book2') },
      { src: '/images/titles-alternate/003.jpg', alt: t('bookCarousel.bookAlts.book3') },
      { src: '/images/titles-alternate/004.jpg', alt: t('bookCarousel.bookAlts.book4') },
      { src: '/images/titles-alternate/005.jpg', alt: t('bookCarousel.bookAlts.book5') },
      { src: '/images/titles-alternate/006.jpg', alt: t('bookCarousel.bookAlts.book6') },
      { src: '/images/titles-alternate/007.jpg', alt: t('bookCarousel.bookAlts.book7') },
      { src: '/images/titles-alternate/008.jpg', alt: t('bookCarousel.bookAlts.book8') },
      { src: '/images/titles-alternate/009.jpg', alt: t('bookCarousel.bookAlts.book9') },
    ],
    [
      { src: '/images/titles-alternate/010.jpg', alt: t('bookCarousel.bookAlts.book10') },
      { src: '/images/titles-alternate/011.jpg', alt: t('bookCarousel.bookAlts.book11') },
      { src: '/images/titles-alternate/012.jpg', alt: t('bookCarousel.bookAlts.book12') },
      { src: '/images/titles-alternate/013.jpg', alt: t('bookCarousel.bookAlts.book13') },
      { src: '/images/titles-alternate/014.jpg', alt: t('bookCarousel.bookAlts.book14') },
      { src: '/images/titles-alternate/015.jpg', alt: t('bookCarousel.bookAlts.book15') },
      { src: '/images/titles-alternate/016.jpg', alt: t('bookCarousel.bookAlts.book16') },
      { src: '/images/titles-alternate/017.jpg', alt: t('bookCarousel.bookAlts.book17') },
      { src: '/images/titles-alternate/018.jpg', alt: t('bookCarousel.bookAlts.book18') },
    ],
  ];

  return (
    <section className="section agency-info py-5 cb-slide-in" ref={addToRefs}>
      <div className="container text-center">
        <h2>{t('bookCarousel.title')}</h2>
        <p className="lead text-muted mb-4">
          {t('bookCarousel.description')}
        </p>
      
          <a href="/contact" className="btn-custom btn-primary btn-lg mb-5 shadow-sm" >{t('bookCarousel.button')}</a>
      

        <div id="cbBookCarousel" className="carousel slide cb-book-carousel" data-bs-ride="carousel">
          <div className="carousel-inner">
            {bookSlides.map((slide, index) => (
              <div className={`carousel-item ${index === 0 ? 'active' : ''}`} key={index}>
                {/* First row - 5 books on desktop, 3 on mobile */}
                <div className="row gx-3 gy-3 justify-content-center">
                  <div className="col-12">
                    <div className="row gx-3 justify-content-center">
                      {slide.slice(0, 5).map((book, bookIndex) => (
                        <div className="col-4 col-md-2-4 col-lg-2" key={bookIndex}>
                          <div className="cb-book-tilt">
                            <img
                              src={book.src}
                              className="img-fluid cb-book-cover"
                              alt={book.alt}
                              loading="lazy"
                            />
                          </div>
                        </div>
                      )).slice(0, window.innerWidth < 768 ? 3 : 5)}
                    </div>
                  </div>
                  
                  {/* Second row - 4 books on desktop, 2 on mobile */}
                  <div className="col-12">
                    <div className="row gx-3 justify-content-center">
                      {slide.slice(5, 9).map((book, bookIndex) => (
                        <div className="col-6 col-md-3 col-lg-2-4" key={bookIndex}>
                          <div className="cb-book-tilt">
                            <img
                              src={book.src}
                              className="img-fluid cb-book-cover"
                              alt={book.alt}
                              loading="lazy"
                            />
                          </div>
                        </div>
                      )).slice(0, window.innerWidth < 768 ? 2 : 4)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Carousel controls */}
          <button
            className="carousel-control-prev cb-carousel-control"
            type="button"
            data-bs-target="#cbBookCarousel"
            data-bs-slide="prev"
            onClick={(e) => e.stopPropagation()}
            aria-label={t('bookCarousel.previous')}
          >
            <span className="carousel-control-prev-icon" aria-hidden="true"></span>
          </button>
          <button
            className="carousel-control-next cb-carousel-control"
            type="button"
            data-bs-target="#cbBookCarousel"
            data-bs-slide="next"
            onClick={(e) => e.stopPropagation()}
            aria-label={t('bookCarousel.next')}
          >
            <span className="carousel-control-next-icon" aria-hidden="true"></span>
          </button>
        </div>
      </div>
    </section>
  );
};

export default BookCarousel;